"use client";

import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";

/** Every case whose full text we can serve (GET /cases/index). */
export interface CaseIndex {
  /** Canonical ref ids, e.g. "nja:2014:s:684". */
  ids: Set<string>;
  /** Canonical ref id → display name ("NJA 2014 s. 684"). */
  display: Map<string, string>;
}

let cached: Promise<CaseIndex> | null = null;

/**
 * Fetch the case index once per page load and share it across components.
 * A failed fetch is not cached, so the next mount retries.
 */
export function loadCaseIndex(): Promise<CaseIndex> {
  if (!cached) {
    cached = (async () => {
      const res = await apiFetch("/cases/index");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { cases?: Record<string, string> };
      const entries = Object.entries(data.cases ?? {});
      return {
        ids: new Set(entries.map(([id]) => id)),
        display: new Map(entries),
      };
    })().catch((e) => {
      cached = null;
      throw e;
    });
  }
  return cached;
}

/** Index hook: null until loaded (or if the fetch failed). */
export function useCaseIndex(): CaseIndex | null {
  const [index, setIndex] = useState<CaseIndex | null>(null);
  useEffect(() => {
    let alive = true;
    loadCaseIndex()
      .then((idx) => {
        if (alive) setIndex(idx);
      })
      .catch(() => {
        /* linkification degrades to plain text */
      });
    return () => {
      alive = false;
    };
  }, []);
  return index;
}
